import { useState, useEffect } from 'react';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { Brain, AlertTriangle, TrendingUp, TrendingDown, CheckCircle, XCircle, DollarSign, Calendar, Target, Lightbulb, ArrowUp, ArrowDown, Minus } from 'lucide-react';
import { formatCurrency } from '@/utils/currency';

const COLORS = ['#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444'];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SpendingInsights({ user, onLogout }) {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('current-month');

  useEffect(() => {
    fetchInsights();
  }, [selectedPeriod]);

  const fetchInsights = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/analytics/spending-insights?period=${selectedPeriod}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInsights(response.data);
    } catch (error) {
      console.error('Error fetching spending insights:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen p-4 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <Navigation user={user} onLogout={onLogout} currentPage="/spending-insights" />
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Analyzing spending patterns...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 lg:p-8 dark:bg-gray-900" data-testid="spending-insights-page">
      <div className="max-w-7xl mx-auto">
        <Navigation user={user} onLogout={onLogout} currentPage="/spending-insights" />

        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center">
                <Brain className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100">Smart Spending Insights</h1>
            </div>
            <p className="text-gray-600 dark:text-gray-400 ml-15">AI-powered analysis of your spending patterns</p>
          </div>
          
          {/* Period Selector */}
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-[200px] dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Select Period" />
            </SelectTrigger>
            <SelectContent className="dark:bg-gray-800">
              <SelectItem value="current-month">This Month</SelectItem>
              <SelectItem value="last-month">Last Month</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Quick Summary Banner */}
        {insights && insights.current_month_spent !== undefined && (
          <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/30 dark:to-pink-900/30 border border-purple-200 dark:border-purple-700 rounded-lg">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 mt-1">
                <Lightbulb className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <span className="font-semibold text-gray-800 dark:text-gray-200">Spending Summary:</span> Spent{' '}
                  <span className="font-bold text-gray-800 dark:text-gray-200">
                    {formatCurrency(insights.current_month_spent, user?.currency)}
                  </span>
                  {' '}this month
                  {insights.over_budget_amount > 0 && (
                    <span>
                      , <span className="font-bold text-gray-800 dark:text-gray-200">
                        {formatCurrency(insights.over_budget_amount, user?.currency)}
                      </span> over budget
                    </span>
                  )}
                  {insights.over_budget_amount === 0 && insights.total_budgeted > 0 && (
                    <span>. Staying within budget! 🎉</span>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Quick Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="glass p-6 dark:bg-gray-800">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
            </div>
            <h3 className="text-sm text-gray-600 dark:text-gray-400 mb-1">This Month</h3>
            <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">{formatCurrency(insights?.current_month_spent || 0, user?.currency)}</p>
          </Card>

          <Card className="glass p-6 dark:bg-gray-800">
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${
                insights?.month_over_month?.direction === 'up' ? 'from-red-400 to-rose-500' : 
                insights?.month_over_month?.direction === 'down' ? 'from-green-400 to-emerald-500' : 
                'from-gray-400 to-gray-500'
              } flex items-center justify-center`}>
                {insights?.month_over_month?.direction === 'up' ? <ArrowUp className="w-6 h-6 text-white" /> : 
                 insights?.month_over_month?.direction === 'down' ? <ArrowDown className="w-6 h-6 text-white" /> : 
                 <Minus className="w-6 h-6 text-white" />}
              </div>
            </div>
            <h3 className="text-sm text-gray-600 dark:text-gray-400 mb-1">vs Last Month</h3>
            <p className={`text-3xl font-bold ${
              insights?.month_over_month?.direction === 'up' ? 'text-red-600 dark:text-red-400' : 
              insights?.month_over_month?.direction === 'down' ? 'text-green-600 dark:text-green-400' : 
              'text-gray-600 dark:text-gray-400'
            }`}>
              {insights?.month_over_month?.change !== undefined ? `${insights.month_over_month.change > 0 ? '+' : ''}${insights.month_over_month.change.toFixed(1)}%` : '0%'}
            </p>
          </Card>

          <Card className="glass p-6 dark:bg-gray-800">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center">
                <Target className="w-6 h-6 text-white" />
              </div>
            </div>
            <h3 className="text-sm text-gray-600 dark:text-gray-400 mb-1">Avg Transaction</h3>
            <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{formatCurrency(insights?.avg_transaction || 0, user?.currency)}</p>
          </Card>

          <Card className="glass p-6 dark:bg-gray-800">
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${
                (insights?.budget_status?.filter(b => b.status === 'over').length || 0) > 0 ? 'from-red-400 to-rose-500' : 'from-green-400 to-emerald-500'
              } flex items-center justify-center`}>
                <AlertTriangle className="w-6 h-6 text-white" />
              </div>
            </div>
            <h3 className="text-sm text-gray-600 dark:text-gray-400 mb-1">Budget Alerts</h3>
            <p className={`text-3xl font-bold ${
              (insights?.budget_status?.filter(b => b.status === 'over').length || 0) > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'
            }`}>
              {insights?.budget_status?.filter(b => b.status === 'over').length || 0}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Over budget</p>
          </Card>
        </div>

        {/* AI Insights Card - Enhanced */}
        <Card className="glass p-6 mb-8 dark:bg-gray-800 border-2 border-purple-200 dark:border-purple-700">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <Brain className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">AI-Powered Insights</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {insights?.current_month_name || 'Current Month'} Analysis
                </p>
              </div>
            </div>
          </div>

          {insights?.ai_insights ? (
            <div className="space-y-4">
              {/* Parse AI insights into individual points */}
              {insights.ai_insights.split(/\n\d+\.\s+|\n•\s+/).filter(point => point.trim()).map((insight, index) => {
                // Clean up markdown formatting
                let trimmedInsight = insight.trim()
                  .replace(/\*\*/g, '')  // Remove markdown bold
                  .replace(/\*/g, '')    // Remove markdown italic
                  .replace(/^\d+\.\s+/, '');  // Remove leading numbers
                
                if (!trimmedInsight) return null;
                
                // Use consistent purple/pink gradient for all cards (matching page theme)
                const iconBg = 'bg-gradient-to-br from-purple-500 to-pink-500';
                const bgColor = 'from-purple-50/50 to-pink-50/50 dark:from-purple-900/10 dark:to-pink-900/10';
                const borderColor = 'border-purple-200 dark:border-purple-700';
                
                // Rotate through icons for variety while keeping consistent colors
                const icons = [Lightbulb, Target, TrendingUp, AlertTriangle];
                const IconComponent = icons[index % icons.length];
                
                return (
                  <div 
                    key={index} 
                    className={`p-5 rounded-xl bg-gradient-to-r ${bgColor} border-2 ${borderColor} hover:shadow-md transition-shadow duration-200`}
                  >
                    <div className="flex items-start space-x-4">
                      <div className={`flex-shrink-0 w-10 h-10 ${iconBg} rounded-lg flex items-center justify-center shadow-md`}>
                        <IconComponent className="w-5 h-5 text-white" />
                      </div>
                      <p className="flex-1 text-gray-800 dark:text-gray-200 leading-relaxed text-[15px]">
                        {trimmedInsight}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
              <Brain className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400 text-lg">No insights available yet</p>
              <p className="text-gray-500 dark:text-gray-500 text-sm mt-2">Add transactions to get personalized AI insights</p>
            </div>
          )}
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Category Distribution - Pie Chart */}
          <Card className="glass p-6 dark:bg-gray-800">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-6">Category Distribution</h2>
            {insights?.top_categories && insights.top_categories.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={insights.top_categories}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ category, percent }) => `${category} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="amount"
                  >
                    {insights.top_categories.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value, user?.currency)} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-gray-500 dark:text-gray-400 py-12">No spending data available</p>
            )}
          </Card>

          {/* Top Spending Categories - Bar Chart */}
          <Card className="glass p-6 dark:bg-gray-800">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-6">Top Spending Categories</h2>
            {insights?.top_categories && insights.top_categories.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={insights.top_categories} margin={{ bottom: 60 }}>
                  <XAxis 
                    dataKey="category" 
                    angle={-45} 
                    textAnchor="end" 
                    height={80}
                    interval={0}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis />
                  <Tooltip formatter={(value) => formatCurrency(value, user?.currency)} />
                  <Bar dataKey="amount" fill="#8b5cf6" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-gray-500 dark:text-gray-400 py-12">No spending data available</p>
            )}
          </Card>
        </div>

        {/* Budget Status */}
        <Card className="glass p-6 mb-8 dark:bg-gray-800">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-6">Budget Status</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {insights?.budget_status && insights.budget_status.length > 0 ? (
              insights.budget_status.map((budget, index) => (
                <div 
                  key={index} 
                  className={`p-4 rounded-xl border-2 ${
                    budget.status === 'over' ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700' : 
                    budget.status === 'warning' ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700' :
                    'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-semibold text-gray-800 dark:text-gray-100">{budget.category}</span>
                    {budget.status === 'over' ? (
                      <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                    ) : budget.status === 'warning' ? (
                      <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    ) : (
                      <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                    )}
                  </div>
                  <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                    <span>{formatCurrency(budget.spent, user?.currency)}</span>
                    <span className="text-gray-500 dark:text-gray-500">/ {formatCurrency(budget.limit, user?.currency)}</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-3 mb-2">
                    <div 
                      className={`h-3 rounded-full ${
                        budget.status === 'over' ? 'bg-red-500 dark:bg-red-400' : 
                        budget.status === 'warning' ? 'bg-amber-500 dark:bg-amber-400' :
                        'bg-green-500 dark:bg-green-400'
                      }`}
                      style={{ width: `${Math.min(budget.percentage, 100)}%` }}
                    ></div>
                  </div>
                  <p className={`text-sm font-semibold ${
                    budget.status === 'over' ? 'text-red-600 dark:text-red-400' : 
                    budget.status === 'warning' ? 'text-amber-600 dark:text-amber-400' :
                    'text-green-600 dark:text-green-400'
                  }`}>
                    {budget.percentage.toFixed(0)}% used
                  </p>
                </div>
              ))
            ) : (
              <div className="col-span-full text-center py-8">
                <Target className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400 text-lg">No budgets set yet</p>
                <p className="text-gray-400 dark:text-gray-500 text-sm mt-2">Create budgets to track your spending limits</p>
              </div>
            )}
          </div>
        </Card>

        {/* Unusual Transactions */}
        {insights?.unusual_transactions && insights.unusual_transactions.length > 0 && (
          <Card className="glass p-6 mb-8 dark:bg-gray-800">
            <div className="flex items-center space-x-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">Unusual Transactions</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {insights.unusual_transactions.map((transaction, index) => (
                <div key={index} className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-xl border border-amber-200 dark:border-amber-700">
                  <p className="font-medium text-gray-800 dark:text-gray-100 mb-1">{transaction.description}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{transaction.category}</p>
                  <p className="text-xl font-bold text-amber-600 dark:text-amber-400">{formatCurrency(transaction.amount, user?.currency)}</p>
                </div>
              ))}
            </div>
          </Card>
        )}

      </div>
      <Footer />
    </div>
  );
}
